import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { requestFormNames, submitTimeOffRequest } from '@/lib/actions';
import { formatDayKey } from '@/lib/dates';
import fullLogo from '@/assets/allied-full-logo.png';

/**
 * The open PTO request form — M2's no-login employee page, the port of the
 * live portal's Employee.html. Reached at #/request with NO session: App.tsx
 * routes here before the login gate, and the only two things this page can do
 * are read the roster's names and call the submit RPC — anon holds nothing
 * else (migrations 0010/0012, pinned in tests/005).
 *
 * Wording is the live form's, verbatim where it had any: the sub-line, the
 * field set (name, dates, notes, Full/Half — lateness and doctor's notes were
 * REMOVED from the employee form on purpose; managers set those via Log an
 * Absence), and the success line. Refusals render exactly as the RPC words
 * them — throttles, duplicate days and closed-day ranges are friendly
 * messages, never blank failures.
 */

type Banner = { kind: 'success' | 'error'; text: string } | null;

/**
 * The browser's own fetch wording (Chrome "Failed to fetch", Firefox
 * "NetworkError…", Safari "Load failed") — the request never reached the
 * server, so the employee's connection IS the problem. Anything else (a 5xx
 * from a paused free-tier project, open item #14; a stale key, trap #19) is
 * the service's problem, and telling that employee to check their wifi
 * would be wrong. Same test LoginScreen.tsx uses.
 */
function isNetworkFailure(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('failed to fetch') ||
    m.includes('networkerror') ||
    m.includes('network request failed') ||
    m.includes('load failed')
  );
}

/**
 * 0032: a PTO denial email links here as `/?resubmit=<request id>#/request`
 * (query before the hash — App.tsx matches '#/request' exactly). The id is
 * passed through untouched; the submit RPC alone decides whether it is valid.
 */
function readResubmitParam(): string | null {
  const value = new URLSearchParams(window.location.search).get('resubmit');
  return value && value.trim() ? value.trim() : null;
}

export function RequestTimeOff() {
  // Pre-P8 punch list, Tier 2 (error handling): the name list used to fail
  // SILENTLY — a load error set `names` to [] and a boolean flag, so the
  // dropdown read "Could not load names", Submit stayed clickable (an empty
  // array is truthy), and clicking it did nothing at all. Now a failed load
  // keeps `names` null (so Submit is genuinely disabled, and visibly so),
  // shows a plain-language banner with a retry button right under the
  // field, and says under the button why it is disabled. `namesAttempt` is
  // the retry trigger: bumping it re-runs the load effect.
  const [names, setNames] = useState<string[] | null>(null);
  const [namesError, setNamesError] = useState<string | null>(null);
  const [namesAttempt, setNamesAttempt] = useState(0);
  // True from tapping "Try again" until that attempt settles. While it is
  // set the banner stays mounted with the button disabled ("Trying again…")
  // rather than unmounting and reappearing identical a few milliseconds
  // later — which read as "the tap did nothing" and dropped focus to <body>
  // (review finding, three lenses).
  const [retrying, setRetrying] = useState(false);
  // After a SUCCESSFUL retry, focus goes to the name field — what the retry
  // was for. Gated by the flag so the initial page load never steals focus.
  const nameSelectRef = useRef<HTMLSelectElement>(null);
  const focusSelectWhenLoaded = useRef(false);
  const [employeeName, setEmployeeName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ptoAmount, setPtoAmount] = useState<'Full' | 'Half'>('Full');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const [resubmitFrom, setResubmitFrom] = useState<string | null>(readResubmitParam);

  useEffect(() => {
    let stale = false;
    setNames(null);
    // Deliberately NOT clearing namesError here — see `retrying` above.
    requestFormNames().then(
      (list) => {
        if (stale) return;
        setNames(list);
        setNamesError(null);
        setRetrying(false);
      },
      (e: unknown) => {
        if (stale) return;
        // The raw text stays in the console for whoever maintains this. On
        // this PUBLIC page it is never shown: it names a database function,
        // and an employee has nobody to read it out to (review consensus).
        // What it DOES tell us — network vs service — drives the headline.
        const message = e instanceof Error ? e.message : String(e);
        console.error('[RequestTimeOff] name list failed to load:', message);
        setNamesError(message);
        setRetrying(false);
      },
    );
    return () => {
      stale = true;
    };
  }, [namesAttempt]);

  useEffect(() => {
    if (names !== null && focusSelectWhenLoaded.current) {
      focusSelectWhenLoaded.current = false;
      nameSelectRef.current?.focus();
    }
  }, [names]);

  function retryNames() {
    focusSelectWhenLoaded.current = true;
    setRetrying(true);
    setNamesAttempt((n) => n + 1);
  }

  const namesFailed = namesError !== null;
  const namesLoading = names === null && !namesFailed;
  const namesEmpty = names !== null && names.length === 0;
  // A retry that has already come back failed: change the words (so the
  // second banner is visibly different from the first) and offer the one
  // remaining lever, a full reload — the same escalation the app's crash
  // fallback uses.
  const retryFailed = namesFailed && namesAttempt > 0 && !retrying;
  const networkProblem = namesFailed && isNetworkFailure(namesError);
  const failureText = retryFailed
    ? networkProblem
      ? 'Still could not load the employee list. Check your connection, or reload the page and try once more.'
      : 'The PTO system is still not responding. Try again in a moment — and if it keeps happening, tell your manager.'
    : networkProblem
      ? 'Could not load the employee list. Check your connection and try again.'
      : 'The PTO system is not responding right now. Try again in a moment — if it keeps happening, tell your manager.';
  const submitDisabled = busy || names === null || namesEmpty;
  // Pre-P8 punch list, Tier 3: "Half day" applies to EVERY day of a range —
  // inherited live-system behaviour (one real half-day record ever), kept
  // as-is, but said out loud the moment it becomes true rather than
  // discovered on the receipt. The <p> below is always mounted (an empty
  // live region that then gains text IS announced; one created already
  // holding text is not — the LoginScreen notice learned the same), and the
  // select points at it, so focusing the select reads it on demand too.
  const halfDayRange = ptoAmount === 'Half' && endDate !== '' && endDate > startDate;
  const halfDayHint = halfDayRange
    ? `Half day will apply to every day from ${formatDayKey(startDate)} to ${formatDayKey(endDate)}. For a half day on just one of them, send that date as its own request.`
    : '';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!employeeName || !startDate) return;
    if (endDate && endDate < startDate) {
      setBanner({ kind: 'error', text: 'End date can’t be before start date.' });
      return;
    }
    setBusy(true);
    setBanner(null);

    const res = await submitTimeOffRequest({
      employeeName,
      startDate,
      endDate: endDate || null,
      ptoAmount,
      notes: notes || null,
      resubmittedFrom: resubmitFrom,
    });

    setBusy(false);
    if (res.ok) {
      // The link is used up: a second request from this same page is an
      // ordinary one, so drop it from state AND from the address bar (a
      // refresh must not quietly re-attach it).
      if (resubmitFrom) {
        setResubmitFrom(null);
        window.history.replaceState(null, '', window.location.pathname + window.location.hash);
      }
      // The live form's confirmation line, with the RPC's receipt appended so
      // a weekend-spanning request shows what was actually recorded.
      const days = res.days_requested ?? 0;
      setBanner({
        kind: 'success',
        text:
          'Request submitted. Your manager will review it shortly. ' +
          `Recorded ${days} workday${days === 1 ? '' : 's'}: ${res.date_range ?? ''}.`,
      });
      setStartDate('');
      setEndDate('');
      setNotes('');
      setPtoAmount('Full');
    } else {
      // `res.detail` (the raw wire text) is deliberately not shown on this
      // public page — see the name-list banner above for the reasoning; it is
      // already in the console via lib/actions.ts. A transport FAILURE also
      // gets this page's own sentence: the shared one says "refresh to see
      // the current state", and an employee has no state to see (M2) — the
      // honest next step here is to ask the manager before sending twice.
      setBanner({
        kind: 'error',
        text:
          res.kind === 'failure'
            ? 'Something went wrong sending this, so it may not have gone through. Check with your manager before sending it again.'
            : (res.error ?? 'Something went wrong. Please try again.'),
      });
    }
  }

  return (
    <main style={S.page}>
      <div style={S.card}>
        <h1 style={S.h1}>
          <img src={fullLogo} alt="Allied Automotive Service" style={S.logo} />
        </h1>
        <h2 style={S.title}>Request Time Off</h2>
        <p style={S.sub}>
          Submit a PTO request. Your manager will review it in the system and you will not
          receive a confirmation email until it is approved or denied.
        </p>
        {resubmitFrom && (
          <p role="status" style={S.resubmitNote}>
            You’re asking for a different date after a request was denied. Choose your name and
            the new dates below — your manager will see that it follows the earlier request.
          </p>
        )}

        <form onSubmit={submit}>
          <label htmlFor="employeeName" style={S.label}>
            Employee Name
          </label>
          <select
            id="employeeName"
            ref={nameSelectRef}
            required
            value={employeeName}
            onChange={(e) => setEmployeeName(e.target.value)}
            disabled={names === null}
            aria-describedby={namesFailed ? 'names-error' : undefined}
            style={S.input}
          >
            <option value="">
              {namesLoading
                ? 'Loading…'
                : namesFailed
                  ? 'Could not load names'
                  : namesEmpty
                    ? 'No names available'
                    : 'Select your name'}
            </option>
            {(names ?? []).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>

          {namesFailed && (
            // Right under the field it explains, with the fix one tap away.
            // role="alert" on the sentence ONLY; the buttons are siblings, so
            // they are neither announced as part of the alert nor read back
            // as the select's description (the same split the ErrorBoundary
            // and LoginScreen banners on this branch use).
            <div style={S.fieldError}>
              <p id="names-error" role="alert" style={S.fieldErrorText}>
                {failureText}
              </p>
              <div style={S.fieldErrorActions}>
                <button type="button" onClick={retryNames} disabled={retrying} style={S.retryBtn}>
                  {retrying ? 'Trying again…' : 'Try again'}
                </button>
                {retryFailed && (
                  <button type="button" onClick={() => window.location.reload()} style={S.reloadBtn}>
                    Reload the page
                  </button>
                )}
              </div>
            </div>
          )}
          {namesEmpty && (
            <p role="status" style={S.fieldError}>
              No employees are listed yet, so a request can’t be submitted from here. Ask your
              manager to add you to the roster.
            </p>
          )}

          <div style={S.row}>
            <div style={S.rowItem}>
              <label htmlFor="startDate" style={S.label}>
                Start Date
              </label>
              <input
                id="startDate"
                type="date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={S.input}
              />
            </div>
            <div style={S.rowItem}>
              <label htmlFor="endDate" style={S.label}>
                End Date
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={S.input}
              />
            </div>
          </div>

          <label htmlFor="ptoAmount" style={S.label}>
            PTO Amount
          </label>
          <select
            id="ptoAmount"
            value={ptoAmount}
            onChange={(e) => setPtoAmount(e.target.value === 'Half' ? 'Half' : 'Full')}
            aria-describedby="ptoAmount-hint"
            style={S.input}
          >
            <option value="Full">Full day</option>
            <option value="Half">Half day (0.5)</option>
          </select>
          <p
            id="ptoAmount-hint"
            role="status"
            aria-live="polite"
            style={halfDayRange ? S.hint : S.hintEmpty}
          >
            {halfDayHint}
          </p>

          <label htmlFor="notes" style={S.label}>
            Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            placeholder="Optional"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ ...S.input, resize: 'vertical' }}
          />

          {/* No stylesheet in this app means no :disabled rule anywhere, and an
              inline background beats the browser's own greying — so without
              buttonDisabled a disabled Submit rendered full brand blue with a
              pointer cursor, i.e. "silently blocked" all over again, just with
              a footnote (review finding). */}
          <button
            type="submit"
            disabled={submitDisabled}
            aria-describedby={namesFailed || namesEmpty ? 'submit-blocked' : undefined}
            style={{ ...S.button, ...(submitDisabled ? S.buttonDisabled : null) }}
          >
            {busy ? 'Submitting…' : 'Submit Request'}
          </button>
          {(namesFailed || namesEmpty) && (
            <p id="submit-blocked" style={S.blockedNote}>
              {namesFailed
                ? 'Submit is unavailable until the employee list loads — use “Try again” above.'
                : 'Submit is unavailable until there is a name to choose.'}
            </p>
          )}

          {banner && (
            <p role={banner.kind === 'error' ? 'alert' : 'status'} style={banner.kind === 'error' ? S.error : S.success}>
              {banner.text}
            </p>
          )}
        </form>
      </div>
      <p style={S.footer}>
        Allied Automotive Service · Attendance &amp; PTO ·{' '}
        <a href="#/login" style={S.footerLink}>
          manager sign-in
        </a>
      </p>
    </main>
  );
}

const S = {
  page: {
    minHeight: 'calc(100vh - 2rem)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, sans-serif',
    padding: '1.5rem',
  },
  card: {
    width: '100%',
    maxWidth: '26rem',
    border: '1px solid #e3e7ee',
    borderRadius: '0.75rem',
    padding: '2rem',
    background: 'white',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
  },
  h1: { margin: '0 0 0.75rem', fontSize: '1.25rem' },
  logo: { display: 'block', height: '90px', width: 'auto', margin: '0 auto' },
  title: { margin: '0 0 0.35rem', fontSize: '1.25rem' },
  sub: { marginTop: 0, marginBottom: '1.5rem', color: '#666', fontSize: '0.88rem', lineHeight: 1.5 },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem' },
  // 16px, not 0.95rem. iOS Safari zooms the whole page in when a text
  // control under 16px takes focus, and never zooms back out on its own — on
  // this form, the app's single highest-traffic phone page, that meant every
  // field tap left the employee scrolled sideways inside a magnified card.
  // Every input/select/textarea in the app now carries 1rem for the same
  // reason (pre-P8 punch list, Tier 2, 2026-09-04); this comment is the
  // canonical explanation, the others point here.
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.6rem 0.7rem',
    fontSize: '1rem',
    border: '1px solid #ccc',
    borderRadius: '0.4rem',
    marginBottom: '1rem',
    fontFamily: 'inherit',
    background: 'white',
  },
  // Side by side when they fit, stacked when they don't (see LogAbsence.tsx's
  // S.row — a 16px date input is ~163px wide on its own).
  row: { display: 'flex', gap: '0.9rem', flexWrap: 'wrap' },
  rowItem: { flex: '1 1 10rem', minWidth: 0 },
  button: {
    width: '100%',
    marginTop: '0.35rem',
    padding: '0.7rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'white',
    background: '#0b46c4',
    border: 0,
    borderRadius: '0.4rem',
    cursor: 'pointer',
  },
  buttonDisabled: { background: '#9db4e0', cursor: 'not-allowed' },
  blockedNote: { margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#666', lineHeight: 1.45 },
  // The inline notices under the name field. Deliberately NOT `error` below:
  // that key also styles the submit-result banner, and a first draft that
  // borrowed it for these notices shifted that banner's margins too (review
  // finding). Same colours, its own spacing.
  fieldError: {
    marginTop: '-0.25rem',
    marginBottom: '1rem',
    padding: '0.7rem 0.85rem',
    borderRadius: '0.4rem',
    background: '#fceaea',
    color: '#b3261e',
    fontSize: '0.88rem',
    lineHeight: 1.5,
  },
  fieldErrorText: { margin: 0 },
  fieldErrorActions: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.6rem' },
  // Brand blue, not the banner's red: it is the recovery action, and the
  // crash fallback's own "Try again" is blue too (review finding on colour
  // hierarchy). 44px tall; full width on a phone.
  retryBtn: {
    flex: '1 1 10rem',
    minHeight: '2.75rem',
    padding: '0.5rem 0.9rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'white',
    background: '#0b46c4',
    border: 0,
    borderRadius: '0.4rem',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  reloadBtn: {
    flex: '1 1 10rem',
    minHeight: '2.75rem',
    padding: '0.5rem 0.9rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1a1a1a',
    background: 'white',
    border: '1px solid #c7d0dd',
    borderRadius: '0.4rem',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  success: {
    marginTop: '1.1rem',
    marginBottom: 0,
    padding: '0.7rem 0.85rem',
    borderRadius: '0.4rem',
    background: '#e6f4ea',
    color: '#1e7e34',
    fontSize: '0.88rem',
    lineHeight: 1.5,
  },
  error: {
    marginTop: '1.1rem',
    marginBottom: 0,
    padding: '0.7rem 0.85rem',
    borderRadius: '0.4rem',
    background: '#fceaea',
    color: '#b3261e',
    fontSize: '0.88rem',
    lineHeight: 1.5,
  },
  // Pre-P8 punch list, Tier 3: #888 on the page's #F3F5F8 background was
  // 3.3:1, under WCAG AA's 4.5:1 for text this size. #5f6368 is 5.5:1 there;
  // the link keeps its underline AND takes the brand blue (7.2:1) so it is
  // told apart from the text by more than colour alone.
  footer: { marginTop: '1.2rem', color: '#5f6368', fontSize: '0.78rem' },
  footerLink: { color: '#0b46c4' },
  // The half-day disclosure sits directly under its select (which carries
  // its own 1rem bottom margin — the negative top margin closes that gap).
  hint: {
    margin: '-0.4rem 0 1rem',
    padding: '0.55rem 0.75rem',
    borderRadius: '0.4rem',
    background: '#fff8e6',
    color: '#946200', // 5.0:1 on #fff8e6
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
  hintEmpty: { margin: 0 },
  // 0032's "request a different date" arrival note — brand-blue tint, not the
  // amber hint or red error: it is context, nothing is wrong.
  resubmitNote: {
    margin: '-0.75rem 0 1.25rem',
    padding: '0.6rem 0.8rem',
    borderRadius: '0.4rem',
    background: '#e8f0fe',
    color: '#0b3a9e',
    fontSize: '0.86rem',
    lineHeight: 1.5,
  },
} satisfies Record<string, React.CSSProperties>;
